<template>
  <div
    class="validation-indicator"
    v-if="hasValidationIssues">
    <!-- Error Indicator -->
    <div
      class="indicator error-indicator"
      v-if="hasErrors">
      <q-icon
        :name="symRoundedError"
        class="text-negative" />
      <q-tooltip class="bg-negative">
        <div class="tooltip-content">
          <div class="tooltip-title">{{ t('composites.BillPartPricingControl.validation.errors') }}</div>
          <ul class="tooltip-list">
            <li
              v-for="error in errors"
              :key="error.message">
              {{ error.message }}
            </li>
          </ul>
        </div>
      </q-tooltip>
    </div>

    <!-- Warning Indicator -->
    <div
      class="indicator warning-indicator"
      v-else-if="hasWarnings && showWarnings">
      <q-icon
        :name="symRoundedWarning"
        class="text-warning" />
      <q-tooltip class="bg-warning text-dark">
        <div class="tooltip-content">
          <div class="tooltip-title">{{ t('composites.BillPartPricingControl.validation.warnings') }}</div>
          <ul class="tooltip-list">
            <li
              v-for="warning in warnings"
              :key="warning.message">
              {{ warning.message }}
            </li>
          </ul>
        </div>
      </q-tooltip>
    </div>

    <!-- Info Indicator -->
    <div
      class="indicator info-indicator"
      v-else-if="hasInfos && showInfos">
      <q-icon
        :name="symRoundedInfo"
        class="text-info" />
      <q-tooltip class="bg-info">
        <div class="tooltip-content">
          <div class="tooltip-title">{{ t('composites.BillPartPricingControl.validation.infos') }}</div>
          <ul class="tooltip-list">
            <li
              v-for="info in infos"
              :key="info.message">
              {{ info.message }}
            </li>
          </ul>
        </div>
      </q-tooltip>
    </div>

    <!-- Badge with count (compact mode) -->
    <q-badge
      v-if="compact && totalIssues > 0"
      :color="badgeColor"
      :label="totalIssues"
      class="validation-badge">
      <q-tooltip :class="`bg-${badgeColor}`">
        <div class="tooltip-content">
          <div class="tooltip-stats">
            <div v-if="errors.length > 0">{{ errors.length }} {{ t('composites.BillPartPricingControl.validation.errorCount') }}</div>
            <div v-if="warnings.length > 0">{{ warnings.length }} {{ t('composites.BillPartPricingControl.validation.warningCount') }}</div>
            <div v-if="infos.length > 0">{{ infos.length }} {{ t('composites.BillPartPricingControl.validation.infoCount') }}</div>
          </div>
        </div>
      </q-tooltip>
    </q-badge>
  </div>
</template>

<script setup lang="ts">
import { symRoundedError, symRoundedInfo, symRoundedWarning } from '@quasar/extras/material-symbols-rounded'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PricingValidationError } from '../types'

interface Props {
  errors?: PricingValidationError[]
  warnings?: PricingValidationError[]
  infos?: PricingValidationError[]
  showWarnings?: boolean
  showInfos?: boolean
  compact?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const props = withDefaults(defineProps<Props>(), {
  errors: () => [],
  warnings: () => [],
  infos: () => [],
  showWarnings: true,
  showInfos: false,
  compact: false,
  size: 'md',
})

const { t } = useI18n()

// Computed properties
const hasErrors = computed(() => props.errors.length > 0)
const hasWarnings = computed(() => props.warnings.length > 0)
const hasInfos = computed(() => props.infos.length > 0)

const hasValidationIssues = computed(() => hasErrors.value || (props.showWarnings && hasWarnings.value) || (props.showInfos && hasInfos.value))

const totalIssues = computed(() => {
  let count = props.errors.length
  if (props.showWarnings) count += props.warnings.length
  if (props.showInfos) count += props.infos.length
  return count
})

const badgeColor = computed(() => {
  if (hasErrors.value) return 'negative'
  if (hasWarnings.value) return 'warning'
  if (hasInfos.value) return 'info'
  return 'grey'
})
</script>

<style scoped lang="scss">
.validation-indicator {
  display: inline-flex;
  align-items: center;

  .indicator {
    display: flex;
    align-items: center;
    cursor: help;

    &.error-indicator {
      .q-icon {
        font-size: 18px;
      }
    }

    &.warning-indicator {
      .q-icon {
        font-size: 18px;
      }
    }

    &.info-indicator {
      .q-icon {
        font-size: 16px;
      }
    }
  }

  .validation-badge {
    cursor: help;
  }

  // Size variations
  &.size-sm {
    .indicator .q-icon {
      font-size: 14px;
    }
  }

  &.size-lg {
    .indicator .q-icon {
      font-size: 22px;
    }
  }
}

.tooltip-content {
  max-width: 300px;

  .tooltip-title {
    font-weight: 500;
    margin-bottom: 4px;
    font-size: 13px;
  }

  .tooltip-list {
    margin: 0;
    padding-left: 16px;
    font-size: 12px;

    li {
      margin-bottom: 2px;
      line-height: 1.3;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .tooltip-stats {
    font-size: 12px;

    div {
      margin-bottom: 2px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
